<template>
   <fragment>
      <TopNav @do-search="doSearch()" />
      <main role="main" class="container">
         <br /><br />

         <div class="row">
            <!-- FILTERS COLUMN -->
            <div class="col-lg-3">
               <FilterGroups ref="filterGroups" show-domains />
            </div>
            <!-- end FILTERS -->

            <!-- RESULTS COLUMN -->
            <div class="col-lg-9">
               <div class="row">
                  <div class="sticky-too">
                     <!-- RESULTS HEADER -->
                     <div class="col-md-8 mb-3 results-header">
                        <h1 class="visibly-hidden">Your search results</h1>
                        <strong class="visibly-hidden">Filtered by:</strong>
                        <FilterButtons @do-search="doSearch()" />
                     </div>
                     <div class="col-md-4 mb-3 results-header tiny-buttons">
                        <SortMenu ref="sortMenu" :disabled="!careers.length" />

                        <div class="btn-group" role="group" aria-label="Grid or list view">
                           <button
                              v-popover
                              ref="tooltip"
                              :class="{'btn-active': view === 'grid'}"
                              :disabled="!careers.length"
                              type="button"
                              class="btn btn-light"
                              data-container="body"
                              data-toggle="popover"
                              data-trigger="hover"
                              data-placement="bottom"
                              data-content="Grid view"
                              @click="view = 'grid'"
                           >
                              <img src="~@/assets/images/icon-grid.png" />
                           </button>
                           <button
                              v-popover
                              ref="tooltip"
                              :class="{'btn-active': view === 'list'}"
                              :disabled="!careers.length"
                              type="button"
                              class="btn btn-light"
                              data-container="body"
                              data-toggle="popover"
                              data-trigger="hover"
                              data-placement="bottom"
                              data-content="List view"
                              @click="view = 'list'"
                           >
                              <img src="~@/assets/images/icon-list.png" />
                           </button>
                        </div>

                        <button
                           v-popover
                           ref="tooltip"
                           :disabled="!careers.length"
                           type="button"
                           aria-label="Share these results"
                           class="btn btn-light btn-share"
                           data-container="body"
                           data-toggle="popover"
                           data-trigger="hover"
                           data-placement="bottom"
                           data-content="Email this filtered view"
                           @click="share()"
                        >
                           <svg
                              width="1em"
                              height="1em"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                              xmlns="http://www.w3.org/2000/svg"
                              class="bi bi-envelope"
                           >
                              <path
                                 fill-rule="evenodd"
                                 d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555zM0 4.697v7.104l5.803-3.558L0 4.697zM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757zm3.436-.586L16 11.801V4.697l-5.803 3.546z"
                              ></path>
                           </svg>
                           Share
                        </button>
                     </div>
                  </div>
                  <!-- end sticky-->

                  <!-- RESULT CARDS -->
                  <div
                     ref="result-list"
                     class="col-md-12 card-list-container card-career"
                     role="list"
                  >
                     <div v-if="loading" class="empty">
                        <div class="spinner-border text-secondary" role="status">
                           <span class="sr-only">Loading</span>
                        </div>
                        <h1>Loading</h1>
                     </div>
                     <div v-else-if="!careers.length" class="empty">
                        <img src="~@/assets/images/icon-empty.png" aria-hidden="true" />

                        <h1>Sorry, no results found.</h1>

                        <p>
                           Try adjusting your search or filters<br />
                           to find what you're looking for.
                        </p>
                        <p>
                           You can also
                           <router-link to="categories">Explore Job Domains</router-link>.
                        </p>
                     </div>

                     <div v-else-if="isGridView" class="row row-cols-1 row-cols-md-3 card-deck">
                        <CareerCard
                           v-for="career in careers"
                           :key="career.jobRoleId + career.shortDomainName"
                           :career="career"
                        />
                     </div>
                     <!-- LIST VIEW -->
                     <div v-else-if="isListView" class="table-responsive-sm">
                        <table class="table table-sm list-view tableFixHead table-hover">
                           <thead>
                              <tr>
                                 <th scope="col"></th>
                                 <th scope="col">Title</th>
                                 <th scope="col">Key competencies</th>
                                 <th scope="col">Job level</th>
                                 <th scope="col">Education</th>
                              </tr>
                           </thead>

                           <tbody>
                              <tr>
                                 <td class="scroll-fix" colspan="5"></td>
                              </tr>

                              <CareerListItem
                                 v-for="career in careers"
                                 :key="career.jobRoleId"
                                 :career="career"
                              />
                           </tbody>
                        </table>
                     </div>
                  </div>
               </div>
            </div>
            <!-- end CARDS -->
         </div>
      </main>
   </fragment>
</template>

<script>
import {debounce} from 'lodash';
import {parse, stringify} from 'query-string';
import TopNav from '@/components/TopNav';
import FilterGroups from '@/components/FilterGroups';
import CareerCard from '@/components/CareerCard';
import CareerListItem from '@/components/CareerListItem';
import FilterButtons from '@/components/FilterButtons';
import SortMenu from '@/components/SortMenu';

export default {
   name: 'CareersPage',
   components: {
      TopNav,
      FilterGroups,
      CareerCard,
      CareerListItem,
      FilterButtons,
      SortMenu,
   },
   data: function () {
      return {
         careers: [],
         loading: false,
         noFilter: true,
         view: 'grid',
         isOverflowing: false,
      };
   },
   computed: {
      isGridView: function () {
         return this.view === 'grid';
      },
      isListView: function () {
         return this.view === 'list';
      },
   },
   created: function () {
      // watch Vuex for changes to the store (filters)
      this.unwatch = this.$store.watch(
         // ignore changes to freetext filter; only monitor checkbox filter changes, in
         // order to avoid triggering a text search unless user clicks Search button (225)
         (state, getters) => [getters['filters/filterParams'], getters['filters/noFilters']],
         debounce(async (watched) => {
            await this.doSearch();
         }, 500)
      );
   },
   beforeDestroy: function () {
      this.unwatch();
   },
   mounted: async function () {
      this.loading = true;
      const query = window.location.search;
      if (query) {
         const filters = parse(query);
         // handle search text here; FilterGroups handles checkbox options
         if (filters.text) {
            this.$store.commit('filters/setFreetext', {
               text: filters.text,
            });
         }
      }

      this.$watch(
         () => this.$refs.sortMenu.sortOption,
         () => {
            this.careers = this.$refs.sortMenu.sortCareers(this.careers);
         }
      );
   },
   methods: {
      isFiltered: function () {
         if (this.$refs.filterGroups) {
            return this.$refs.filterGroups.isFiltered;
         }
         return false;
      },
      checkOverflow: function () {
         const el = this.$refs['result-list'];
         this.isOverflowing = el.offsetHeight < el.scrollHeight;
      },
      updateCounts: function (filtered) {
         const groups = this.$refs.filterGroups;
         if (!groups) return;
         groups.isFiltered = filtered;
         groups.resultCount = 0;
         groups.resultCount = this.careers.length;
      },
      doSearch: async function () {
         const query = this.$store.getters['filters/allParams'];
         const search = stringify(query);
         this.noFilter = this.$store.getters['filters/empty'];

         console.log('doSearch', query);

         // update the URL to reflect selected filters and allow bookmarking
         try {
            this.$router.push({path: 'careers', query});
         } catch (e) {}
         // update the search results using the API
         try {
            this.loading = true;
            const careers = (await this.$apiGet(`Careers/Find?${search}`)).data;
            this.careers = this.$refs.sortMenu.sortCareers(careers);
         } catch (e) {
            this.careers = [];
            if (e.response.status === 404) {
               console.debug('no results');
            }
         } finally {
            this.loading = false;
         }
         this.updateCounts(!this.noFilter);
         await this.$nextTick();
         this.checkOverflow();
      },
      share: function () {
         if (!this.careers.length) return;
         const mailto =
            'mailto:?subject=Jobs%20on%20Data%20Center%20Career%20Pathfinder&body=' +
            encodeURIComponent(window.location.href);
         console.log(window.location.href);
         window.open(mailto);
      },
   },
};
</script>



// WEBPACK FOOTER //
// src/components/CareersPage.vue